import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  Building2,
  Grid3x3,
  IndianRupee,
  LayoutDashboard,
  LogOut,
  Menu,
  ScrollText,
  Send,
  Settings,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { formatINRCompact, istToday } from '@gbv/utils';
import { Avatar, Logo, Skeleton, cn, useDesktopNotifications, useToast } from '@gbv/ui';
import { useAuth } from '../stores/auth';
import { api } from '../lib/api';
import { connectSocket, disconnectSocket, onServerEvent } from '../lib/socket';
import { FloatingChat } from './FloatingChat';

/**
 * The admin console shell — same three-column shape as the employee
 * workspace, so moving between them costs nothing to learn. The right rail
 * carries platform health rather than one client's tasks, because that is what
 * an administrator is actually watching.
 */

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  end?: boolean;
  planned?: boolean;
}

interface NavGroup {
  department?: string;
  items: NavItem[];
}

const NAVIGATION: NavGroup[] = [
  {
    items: [{ label: 'Overview', to: '/', icon: LayoutDashboard, end: true }],
  },
  {
    department: 'Accounts',
    items: [
      { label: 'Employees', to: '/employees', icon: Users },
      { label: 'Clients', to: '/clients', icon: Building2 },
      { label: 'Assignments', to: '/assignments', icon: Grid3x3 },
    ],
  },
  {
    department: 'Platform',
    items: [
      { label: 'Settings', to: '/settings', icon: Settings },
      { label: 'Admin accounts', to: '/admins', icon: ShieldCheck },
      { label: 'Sign-in activity', to: '/sign-ins', icon: ShieldCheck },
      { label: 'Audit log', to: '/audit', icon: ScrollText },
      { label: 'System health', to: '/health', icon: Activity, planned: true },
    ],
  },
  {
    department: 'Commercial',
    items: [
      { label: 'Billing', to: '/billing', icon: IndianRupee, planned: true },
      { label: 'Usage limits', to: '/usage', icon: AlertTriangle, planned: true },
    ],
  },
];

export function AdminShell({ children }: { children: ReactNode }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const location = useLocation();

  const selfId = useAuth((s) => s.user?.id);
  const toast = useToast();
  const queryClient = useQueryClient();
  const desktop = useDesktopNotifications('admin');

  useEffect(() => {
    connectSocket();
    return () => disconnectSocket();
  }, []);

  /**
   * Two channels, and the difference matters.
   *
   * alert:new is the console's reason to exist — a client with no report sent
   * by 18:35, a limit breached, an invoice gone overdue. Nobody addressed it
   * to a person; it is the platform saying something is wrong, so it lands as
   * a warning and refreshes the count in the rail.
   *
   * notification:new is addressed to you by name — a ping, a reminder someone
   * set for you. Away from the tab either one becomes a desktop notification;
   * with the tab open it stays inside the page. Never both.
   */
  useEffect(
    () =>
      onServerEvent('alert:new', (payload) => {
        void queryClient.invalidateQueries({ queryKey: ['admin-summary'] });

        const title = payload.severity === 'DANGER' ? 'Needs attention now' : 'New alert';
        const shown = desktop.notify({
          title,
          body: payload.message,
          link: payload.link,
          // No id on this event, so the text is the identity: the same alert
          // raised twice replaces itself instead of stacking.
          tag: payload.message,
        });

        if (!shown) {
          if (payload.severity === 'DANGER') toast.error(title, payload.message);
          else toast.info(title, payload.message);
        }
      }),
    [queryClient, desktop, toast],
  );

  useEffect(
    () =>
      onServerEvent('notification:new', (payload) => {
        // Skip what this person just did themselves. The confirmation is
        // already on screen, and a second one describing it in the third
        // person reads like somebody else acted.
        if (payload.actorId && payload.actorId === selfId) return;

        const shown = desktop.notify({
          title: payload.title,
          body: payload.body,
          link: payload.link,
          tag: payload.id,
        });
        if (!shown) toast.info(payload.title, payload.body);
      }),
    [desktop, toast, selfId],
  );

  return (
    <div className="flex h-screen overflow-hidden bg-surface-page">
      {/* No icon rail here. Every shortcut it held — Overview, Employees,
          Clients, Assignments — already sits in the nav beside it, so it was
          52px spent on a duplicate. */}
      <ConsoleNav className="hidden lg:flex" />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-topbar shrink-0 items-center gap-3 border-b border-line bg-white px-4 lg:px-6">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open navigation"
            className="rounded-control p-1.5 text-ink-muted transition-colors hover:bg-surface-sunken lg:hidden"
          >
            <Menu size={18} />
          </button>

          <span className="rounded-control border border-bee/40 bg-bee-light px-3 py-1.5 text-[12.5px] font-semibold text-bee-dark">
            Admin console · all clients
          </span>

          <span className="ml-auto truncate font-mono text-[10px] uppercase tracking-[0.16em] text-ink-soft">
            {location.pathname === '/' ? 'Overview' : location.pathname.slice(1)}
          </span>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto p-content-sm md:p-content">{children}</main>
      </div>

      <PlatformRail />

      {/* Shell root, not inside ConsoleNav: that column is `hidden` below lg,
          and the mobile drawer renders a second copy of it. */}
      <FloatingChat />

      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-ink/50"
            onClick={() => setMobileNavOpen(false)}
            role="presentation"
          />
          <ConsoleNav
            className="absolute left-0 top-0 flex h-full"
            onNavigate={() => setMobileNavOpen(false)}
            onClose={() => setMobileNavOpen(false)}
          />
        </div>
      )}
    </div>
  );
}

function ConsoleNav({
  className,
  onNavigate,
  onClose,
}: {
  className?: string;
  onNavigate?: () => void;
  onClose?: () => void;
}) {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);

  return (
    <div className={cn('w-[212px] shrink-0 flex-col border-r border-white/5 bg-auth-panel', className)}>
      <div className="flex h-topbar shrink-0 items-center gap-2 px-4">
        {/* The mark used to live in the icon rail; it moves here so the brand
            is still the first thing in the column. */}
        <Logo size={24} showWordmark wordmarkSize={18} />
        <span className="rounded-pill bg-bee/15 px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.16em] text-bee">
          Admin
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="ml-auto rounded p-1 text-white/40 hover:text-white"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        {NAVIGATION.map((group, index) => (
          <div key={group.department ?? `top-${index}`} className={cn(index > 0 && 'mt-4')}>
            {group.department && (
              <p className="px-2 pb-1 font-mono text-[9px] uppercase tracking-[0.16em] text-white/25">
                {group.department}
              </p>
            )}
            <div className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-control px-2 py-1.5 text-[12.5px] transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-auth-gold',
                      isActive
                        ? 'bg-auth-gold/15 font-semibold text-auth-gold'
                        : 'text-white/45 hover:bg-white/5 hover:text-white/85',
                    )
                  }
                >
                  <item.icon size={14} className="shrink-0" />
                  <span className="truncate">{item.label}</span>
                  {item.planned && (
                    <span className="ml-auto shrink-0 font-mono text-[8px] uppercase tracking-wider text-white/20">
                      soon
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="shrink-0 border-t border-white/5 px-3 py-3">
        <div className="flex items-center gap-2">
          <Avatar name={user?.name ?? '?'} src={user?.avatarUrl} size="sm" />
          <div className="min-w-0">
            <p className="truncate text-[12px] font-semibold text-white/85">{user?.name}</p>
            <p className="truncate font-mono text-[9px] uppercase tracking-wider text-white/30">
              Super admin
            </p>
          </div>
        </div>
        <NavLink
          to='/account'
          className={({ isActive }) =>
            cn(
              'mt-2 flex items-center gap-1.5 text-[11px] font-semibold transition-colors',
              isActive ? 'text-white' : 'text-white/40 hover:text-white/80',
            )
          }
        >
          <Settings size={12} />
          Settings
        </NavLink>
        <button
          type="button"
          onClick={() => void logout()}
          className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-white/40 transition-colors hover:text-danger"
        >
          <LogOut size={12} />
          Log out
        </button>
      </div>
    </div>
  );
}

interface PlatformSummary {
  clients: { total: number; active: number; onboarding: number };
  employees: { total: number; active: number };
  mrrPaise: number;
  leads: number;
  reportsSentToday: number;
  openAlerts: number;
}

/** Platform health, not one client's tasks — what an admin actually watches. */
function PlatformRail() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-summary'],
    queryFn: () => api.get<PlatformSummary>('/summary', { skipOrg: true }),
    refetchInterval: 60_000,
  });

  const outstanding = data ? data.clients.active - data.reportsSentToday : 0;

  return (
    <aside className="hidden w-[268px] shrink-0 flex-col gap-3 overflow-y-auto border-l border-line bg-surface-page px-3 py-4 scrollbar-thin 2xl:flex">
      <p className="px-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-soft">
        Platform today
      </p>
      <p className="-mt-2 px-1 font-mono text-[10px] tabular text-ink-soft">{istToday()} · IST</p>

      <RailCard title="Reports sent" icon={<Send size={13} />}>
        {isLoading ? (
          <Skeleton className="h-10" />
        ) : (
          <div className="flex items-center gap-3">
            <span className="tabular text-2xl font-bold text-ink">
              {data!.reportsSentToday}
              <span className="text-sm font-normal text-ink-soft">/{data!.clients.active}</span>
            </span>
            <span
              className={cn(
                'text-[11px] leading-tight',
                outstanding === 0 ? 'text-go-dark' : 'text-bee-dark',
              )}
            >
              {outstanding === 0 ? 'Every client has today’s report.' : `${outstanding} still to send.`}
            </span>
          </div>
        )}
      </RailCard>

      <RailCard title="Open alerts" icon={<AlertTriangle size={13} />}>
        {isLoading ? (
          <Skeleton className="h-10" />
        ) : (
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'tabular text-2xl font-bold',
                data!.openAlerts === 0 ? 'text-ink' : 'text-danger',
              )}
            >
              {data!.openAlerts}
            </span>
            <span className="text-[11px] leading-tight text-ink-muted">
              {data!.openAlerts === 0 ? 'Nothing needs you.' : 'Waiting on someone.'}
            </span>
          </div>
        )}
      </RailCard>

      <RailCard title="Accounts" icon={<Users size={13} />}>
        {isLoading ? (
          <Skeleton className="h-14" />
        ) : (
          <dl className="flex flex-col gap-1 text-[12px]">
            <Row label="Active clients" value={String(data!.clients.active)} />
            <Row label="Onboarding" value={String(data!.clients.onboarding)} />
            <Row label="Employees" value={String(data!.employees.active)} />
            <Row label="Leads managed" value={data!.leads.toLocaleString('en-IN')} />
          </dl>
        )}
      </RailCard>

      <RailCard title="Revenue" icon={<IndianRupee size={13} />}>
        {isLoading ? (
          <Skeleton className="h-8" />
        ) : (
          <>
            <p className="tabular text-2xl font-bold text-ink">{formatINRCompact(data!.mrrPaise)}</p>
            <p className="text-[11px] text-ink-soft">Monthly recurring, active clients</p>
          </>
        )}
      </RailCard>
    </aside>
  );
}

function RailCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-card border border-line bg-white px-3 py-3">
      <header className="mb-2 flex items-center gap-1.5">
        <span className="text-ink-soft">{icon}</span>
        <h2 className="text-[12px] font-bold text-ink">{title}</h2>
      </header>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="tabular font-semibold text-ink">{value}</dd>
    </div>
  );
}
